import { inject, Injectable } from '@angular/core';

import { KeycloakProfile } from 'keycloak-js';
import { MessageService } from './message.service';

export const PROFILE_KEY = 'ang-profile';
@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private readonly keycloak = (window as any).keycloak;
  private messageService = inject(MessageService);

  profile: KeycloakProfile;
  token: string;

  constructor() {
    // Keycloak is now initialized in main.ts before Angular bootstrap
    if (this.keycloak && this.keycloak.authenticated) {
      console.log('User is authenticated, loading profile...');
      // Load profile if user is authenticated
      if (!localStorage[PROFILE_KEY]) {
        console.log('No cached profile, loading from Keycloak...');
        this.loadProfile();
      } else {
        // Load from cache
        console.log('Loading profile from cache...');
        this.profile = JSON.parse(localStorage[PROFILE_KEY]);
        // Extract token from profile attributes
        const profileAttrs = (this.profile as any)?.attributes;
        if (profileAttrs && profileAttrs['token']) {
          this.token = Array.isArray(profileAttrs['token'])
            ? profileAttrs['token'][0]
            : profileAttrs['token'];
          console.log('Token loaded from cached profile');
        } else {
          console.warn('No token in cached profile, reloading...');
          this.loadProfile();
        }

        // Set up token refresh
        this.setupTokenRefresh();

        // Notify that profile is ready
        window.dispatchEvent(new Event('profile:loaded'));
      }
    } else if (localStorage[PROFILE_KEY]) {
      // Fallback to cached profile if available
      console.warn('Not authenticated but found cached profile, using it...');
      this.profile = JSON.parse(localStorage[PROFILE_KEY]);
      const profileAttrs = (this.profile as any)?.attributes;
      if (profileAttrs && profileAttrs['token']) {
        this.token = Array.isArray(profileAttrs['token'])
          ? profileAttrs['token'][0]
          : profileAttrs['token'];
      }
    } else {
      console.warn('No profile found - user needs to log in');
    }
  }

  getEmail(): string {
    return this.profile.email;
  }
  getFirstName(): string {
    return this.profile.firstName;
  }
  getLastName(): string {
    return this.profile.lastName;
  }
  getToken(): string {
    // Return the 'token' user attribute which contains the Flowable password
    const profileAttrs = (this.profile as any)?.attributes;
    if (this.profile && profileAttrs && profileAttrs['token']) {
      return Array.isArray(profileAttrs['token'])
        ? profileAttrs['token'][0]
        : profileAttrs['token'];
    }
    // Fallback to cached token
    return this.token;
  }
  getUsername(): string {
    return this.profile.username;
  }
  isAuthenticated(): boolean {
    console.debug(`Checking authentication status, profile loaded: ${!!this.profile}`);
    return !!this.profile;
  }

  private setupTokenRefresh() {
    // Token is a user attribute (Flowable password), not a JWT that needs refreshing
    // But we still need to refresh the Keycloak session token to keep SSO active
    if (!this.keycloak) return;

    setInterval(() => {
      this.keycloak.updateToken(70).then((refreshed) => {
        if (refreshed) {
          console.debug('Keycloak session refreshed');
        }
      }).catch(() => {
        console.error('Failed to refresh Keycloak session');
        this.messageService.set('Session expired. Please log in again.', 'error');
      });
    }, 60000);
  }

  loadProfile() {
    this.keycloak.loadUserProfile().then((profile) => {
      this.profile = profile as KeycloakProfile;

      // The profile may not include custom attributes by default
      // Fetch full account information including attributes
      const accountUrl = `${this.keycloak.authServerUrl}/realms/${this.keycloak.realm}/account`;

      fetch(accountUrl, {
        headers: {
          'Accept': 'application/json',
          'Authorization': 'Bearer ' + this.keycloak.token
        }
      })
      .then(response => response.json())
      .then((accountData: any) => {
        console.log('Account data loaded with attributes');

        // Merge account attributes into profile
        if (accountData.attributes) {
          (this.profile as any).attributes = accountData.attributes;
        }

        // Extract Flowable password from user attributes
        const profileAttrs = accountData.attributes;
        if (profileAttrs && profileAttrs['token']) {
          this.token = Array.isArray(profileAttrs['token'])
            ? profileAttrs['token'][0]
            : profileAttrs['token'];
          console.log('Flowable token extracted from user attributes');
        } else {
          console.warn('No token attribute found in Keycloak account data');
        }

        localStorage.setItem(PROFILE_KEY, JSON.stringify(this.profile));
        localStorage.setItem('ang-token', this.keycloak.token);
        localStorage.setItem('ang-refresh-token', this.keycloak.refreshToken);

        this.setupTokenRefresh();

        this.messageService.set(`Welcome back, ${this.getFirstName()}!`);

        window.dispatchEvent(new Event('profile:loaded'));
      })
      .catch(error => {
        console.error('Failed to load account attributes:', error);

        // Fallback: still save basic profile
        localStorage.setItem(PROFILE_KEY, JSON.stringify(this.profile));
        localStorage.setItem('ang-token', this.keycloak.token);
        localStorage.setItem('ang-refresh-token', this.keycloak.refreshToken);

        this.setupTokenRefresh();
        this.messageService.set(`Welcome back, ${this.getFirstName()}!`);
        window.dispatchEvent(new Event('profile:loaded'));
      });
    }).catch(() => {
      alert('Failed to load user profile');
    });
  }
  login() {
    console.log('Logging in with Keycloak');
    this.keycloak.login({ redirectUri: window.location.origin + '/dashboard' }).then(() => {
      console.log('Login initiated');
      this.loadProfile();
    }).catch(() => {
      console.error('Login failed');
      this.messageService.set(`Authentication failed, please try again or contact support.`, 'error');
    });
  }
  logout() {
    console.log('Logging out from Keycloak');
    localStorage.removeItem(PROFILE_KEY);
    localStorage.removeItem('ang-token');
    localStorage.removeItem('ang-refresh-token');
    this.keycloak.logout({ redirectUri: window.location.origin + '/dashboard' });
  }
}
